/**
 * Utilidades para aplicar la configuración de sidebar dinámico (módulos ocultos +
 * orden/agrupación visual) sobre el árbol de rutas real (routes/index.js -> getRoutes()),
 * sin duplicar ese árbol ni recalcular la URL real de una página según dónde quede
 * agrupada visualmente.
 *
 * Cada nodo del árbol real conserva su `fullPath` (calculado una sola vez, aquí) como
 * identificador estable: mover un nodo a otro grupo visual con applyVisualOrder() nunca
 * cambia su fullPath, solo su posición/agrupación en el árbol que se pinta.
 */

/**
 * Anota cada nodo del árbol con su fullPath real (parentPath + path propio),
 * exactamente como ya lo hacían getMenuItems()/flatRoutes(), pero conservando
 * la forma del árbol para poder operar sobre ella después.
 */
export const withFullPaths = (routes = [], parentPath = '') => routes.map((route) => {
  const fullPath = `${parentPath}${route.path}`;
  const children = route.children?.length
    ? withFullPaths(route.children, fullPath)
    : undefined;
  return {
    ...route,
    fullPath,
    ...(children ? { children } : {}),
  };
});

/**
 * Quita del árbol (recursivamente) cualquier nodo cuyo fullPath esté en hiddenPaths.
 * Un grupo se quita completo si su propio fullPath está oculto; si no está oculto pero
 * se queda sin hijos visibles, también se quita (no tiene sentido un grupo vacío).
 */
export const filterHiddenFromTree = (tree = [], hiddenPaths = []) => tree
  .map((node) => {
    if (hiddenPaths.includes(node.fullPath)) return null;
    if (!node.children?.length) return node;
    const children = filterHiddenFromTree(node.children, hiddenPaths);
    if (!children.length) return null;
    return { ...node, children };
  })
  .filter(Boolean);

const flattenByFullPath = (tree = [], map = new Map()) => {
  tree.forEach((node) => {
    map.set(node.fullPath, node);
    if (node.children?.length) flattenByFullPath(node.children, map);
  });
  return map;
};

/**
 * menuStructure: [{ path, children: [...] }, ...] (solo path + children, sin metadata
 * de la página — título/ícono/permiso siempre se resuelven en vivo contra el árbol real).
 * Reconstruye el árbol visual en ese orden/agrupación. Cualquier nodo del árbol real que
 * no aparezca en menuStructure se agrega al final, respetando su padre original, para no
 * perder de vista páginas nuevas que se agreguen después de guardar el orden.
 */
export const applyVisualOrder = (tree = [], menuStructure = []) => {
  if (!menuStructure?.length) return tree;

  const porFullPath = flattenByFullPath(tree);
  const usados = new Set();

  const construirDesdeEstructura = (estructura) => estructura
    .map((item) => {
      const nodoReal = porFullPath.get(item.path);
      if (!nodoReal) return null; // la página ya no existe en el árbol real
      usados.add(item.path);
      const hijosGuardados = item.children?.length
        ? construirDesdeEstructura(item.children)
        : [];
      const hijosNuevos = nodoReal.children?.length
        ? nodoReal.children.filter((hijo) => !usados.has(hijo.fullPath))
        : [];
      hijosNuevos.forEach((hijo) => usados.add(hijo.fullPath));
      const children = [...hijosGuardados, ...hijosNuevos];
      return {
        ...nodoReal,
        openByDefault: item.openByDefault === true,
        ...(nodoReal.children?.length ? { children } : {}),
      };
    })
    .filter(Boolean);

  const visual = construirDesdeEstructura(menuStructure);
  const nuevosEnRaiz = tree.filter((node) => !usados.has(node.fullPath));
  return [...visual, ...nuevosEnRaiz];
};

/**
 * Convierte el árbol visual (ya con hidden+order aplicados) a la forma liviana que se
 * persiste en el backend: path + children + openByDefault, nunca título/ícono/permiso.
 */
export const treeToMenuStructure = (tree = []) => tree.map((node) => ({
  path: node.fullPath,
  openByDefault: node.openByDefault === true,
  ...(node.children?.length ? { children: treeToMenuStructure(node.children) } : {}),
}));

/**
 * Recorre el árbol visual y devuelve el fullPath de todos los grupos marcados para
 * abrirse expandidos por defecto ("pin"). Se usa para que esos grupos empiecen abiertos
 * sin importar en qué página esté parado el usuario, además de los que abre la
 * navegación normal (ver findVisualAncestorChain).
 */
export const collectOpenByDefaultPaths = (tree = []) => tree.reduce((acc, node) => {
  if (node.openByDefault && node.children?.length) acc.push(node.fullPath);
  if (node.children?.length) acc.push(...collectOpenByDefaultPaths(node.children));
  return acc;
}, []);

/**
 * Busca en el árbol visual el nodo cuyo fullPath === targetFullPath y devuelve la cadena
 * de fullPath de todos sus ancestros visuales + el suyo propio (root primero). Devuelve
 * null si no lo encuentra (ej. quedó oculto). Se usa para calcular selectedKeys/openKeys
 * según la posición VISUAL actual, no según el prefijo de la URL.
 */
export const findVisualAncestorChain = (tree = [], targetFullPath, chainSoFar = []) => {
  for (let i = 0; i < tree.length; i += 1) {
    const node = tree[i];
    const chain = [...chainSoFar, node.fullPath];
    if (node.fullPath === targetFullPath) return chain;
    if (node.children?.length) {
      const found = findVisualAncestorChain(node.children, targetFullPath, chain);
      if (found) return found;
    }
  }
  return null;
};
